// src/pages/autopilot/ResultsReview.js
import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import {
  setVerifiedProspects,
  toggleProspectSelection,
  goToPreviousStep,
  goToNextStep
} from '../../store/slices/autoPilotSlice';
import ProspectCard from '../../components/autopilot/ProspectCard';

function ResultsReview({ onNext, onBack }) {
  const dispatch = useDispatch();
  const {
    searchResults,
    verifiedProspects,
    selectedProspects
  } = useSelector((state) => state.autoPilot);

  useEffect(() => {
    // Set verified prospects from search results (up to 20) if not already set
    if (searchResults.length > 0 && verifiedProspects.length === 0) {
      const prospects = searchResults.slice(0, 20).map(prospect => ({
        ...prospect,
        matchScore: calculateMatchScore(prospect),
        enriched: prospect.enriched || false,
        verified: prospect.verified || true
      }));
      dispatch(setVerifiedProspects(prospects));
    }
  }, [searchResults, verifiedProspects.length, dispatch]);

  const calculateMatchScore = (prospect) => {
    // Simple match score calculation (can be enhanced)
    let score = 50; // Base score
    if (prospect.title) score += 10;
    if (prospect.company) score += 10;
    if (prospect.location) score += 10;
    if (prospect.experience) score += 10;
    if (prospect.skills && prospect.skills.length > 0) score += 10;
    return Math.min(100, score);
  };

  const handleToggleProspect = (prospectId) => {
    dispatch(toggleProspectSelection(prospectId));
  };

  const handleContinue = () => {
    if (selectedProspects.length === 0) {
      alert('Please select at least one prospect to continue.');
      return;
    }
    if (onNext) {
      onNext();
    } else {
      dispatch(goToNextStep());
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      dispatch(goToPreviousStep());
    }
  };

  const prospectsToShow = verifiedProspects.length > 0 ? verifiedProspects : searchResults.slice(0, 20);
  const selectedIds = selectedProspects.map(p => p._id || p.id);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Review Results
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
        Review the filtered and enriched prospects below. Select up to 20 prospects to add to your list.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{selectedProspects.length}</strong> of <strong>{prospectsToShow.length}</strong> prospects selected
          {selectedProspects.length >= 20 && ' (Maximum reached)'}
        </Typography>
      </Alert>

      {prospectsToShow.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
            No prospects found
          </Typography>
          <Typography variant="body2" sx={{ color: '#888' }}>
            Try adjusting your search criteria or go back to modify the ICP.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>
          {prospectsToShow.map((prospect, index) => (
            <ProspectCard
              key={prospect._id || prospect.id || index}
              prospect={prospect}
              selected={selectedIds.includes(prospect._id || prospect.id)}
              onToggle={handleToggleProspect}
              showCheckbox={true}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: '#666',
            color: '#666',
            '&:hover': { borderColor: '#333', bgcolor: '#f5f5f5' }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={selectedProspects.length === 0}
          endIcon={<ArrowForwardIcon />}
          sx={{
            bgcolor: '#1BE885',
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#20ec84' },
            '&:disabled': { bgcolor: '#ccc' }
          }}
        >
          Continue to List Creation ({selectedProspects.length} selected)
        </Button>
      </Box>
    </Box>
  );
}

export default ResultsReview;
